import { useEffect, useRef, useCallback } from 'react';
import { X, History, Sparkles, Loader2, MapPin } from 'lucide-react';
import { useChatContext } from '../../contexts/ChatContext';
import { useChatMessages, useSendMessage, type ChatMessage as ChatMessageType } from '../../hooks/useChatMessages';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatHistorySidebar from './ChatHistorySidebar';

export default function ChatPanel() {
  const {
    isOpen,
    setIsOpen,
    conversationId,
    setConversationId,
    showHistory,
    setShowHistory,
    pageContext,
  } = useChatContext();

  const { data: messages, isLoading: loadingMessages } = useChatMessages(conversationId);
  const sendMessage = useSendMessage();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const optimisticRef = useRef<ChatMessageType[]>([]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sendMessage.isPending]);

  // Clear optimistic messages when real data arrives
  useEffect(() => {
    if (messages && messages.length > 0) {
      optimisticRef.current = [];
    }
  }, [messages]);

  const handleSend = useCallback((text: string, images?: Array<{ base64: string; mediaType: string }>) => {
    // Add optimistic user message
    const tempMsg: ChatMessageType = {
      id: 'temp-' + Date.now(),
      conversation_id: conversationId || '',
      role: 'user',
      content: text,
      metadata: {},
      created_at: new Date().toISOString(),
    };
    optimisticRef.current = [...optimisticRef.current, tempMsg];

    sendMessage.mutate(
      { conversationId, message: text, context: pageContext, images },
      {
        onSuccess: (data) => {
          if (!conversationId) {
            setConversationId(data.conversation_id);
          }
          optimisticRef.current = [];
        },
        onError: () => {
          optimisticRef.current = [];
        },
      }
    );
  }, [conversationId, pageContext, sendMessage, setConversationId]);

  if (!isOpen) return null;

  const allMessages = [
    ...(messages || []),
    ...optimisticRef.current,
  ];

  const contextLabel = pageContext.projectId
    ? `Project`
    : pageContext.leadId
    ? `Lead`
    : pageContext.tab || '';

  return (
    <div className="fixed bottom-20 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[400px] h-[min(600px,calc(100vh-8rem))] bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-600 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-theme-border bg-theme-surface/50">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-brand" />
          <span className="font-semibold text-sm text-theme-primary">Pracht AI</span>
          {contextLabel && (
            <span className="flex items-center gap-0.5 text-[10px] text-theme-tertiary bg-theme-surface px-1.5 py-0.5 rounded-full">
              <MapPin className="w-2.5 h-2.5" />
              {contextLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`p-1.5 rounded-lg transition-colors ${showHistory ? 'text-brand bg-brand/10' : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-surface'}`}
            title="Gesprekgeschiedenis"
          >
            <History className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 text-theme-secondary hover:text-theme-primary transition-colors rounded-lg hover:bg-theme-surface"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 relative">
        {showHistory && <ChatHistorySidebar />}

        {!showHistory && (
          <>
            {loadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-5 h-5 animate-spin text-theme-tertiary" />
              </div>
            ) : allMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <Sparkles className="w-8 h-8 text-brand/40 mb-3" />
                <p className="text-sm font-medium text-theme-primary mb-1">Hoe kan ik je helpen?</p>
                <p className="text-xs text-theme-tertiary">
                  Stel vragen over projecten, facturen, planning of vraag om vakadvies.
                </p>
              </div>
            ) : (
              allMessages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  role={msg.role as 'user' | 'assistant'}
                  content={msg.content}
                  metadata={msg.metadata}
                  timestamp={msg.created_at}
                />
              ))
            )}

            {sendMessage.isPending && (
              <div className="flex items-center gap-2 text-theme-tertiary">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-brand/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-brand/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-brand/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs">Even denken...</span>
              </div>
            )}

            {sendMessage.isError && (
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-2 text-xs text-red-600 dark:text-red-400">
                {sendMessage.error?.message || 'Er ging iets mis. Probeer het opnieuw.'}
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      {!showHistory && (
        <ChatInput onSend={handleSend} disabled={sendMessage.isPending} />
      )}
    </div>
  );
}
